import usePowerBiReportsData from "@app/data-hooks/use-power-bi-reports-data.ts";
import {
    Button,
    Checkbox,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormLabel,
    Radio,
    RadioGroup,
    Stack,
} from "@mui/material";
import { useForm } from "@tanstack/react-form";
import { Dialog } from "bi-lib";

interface Props {
    open: boolean;
    onClose: () => void;
    invoiceId: number;
}

export default function InvoicePrintModal({ open, onClose, invoiceId }: Props) {
    const { generateReport } = usePowerBiReportsData();

    const form = useForm({
        defaultValues: {
            reportId: 31,
            sortId: 1,
            printReceiptReport: false,
        },
        onSubmit: async ({ value }) => {
            const promises = [];
            promises.push(
                generateReport.mutateAsync({
                    reportId: value.reportId,
                    parameters: {
                        invoiceId: invoiceId,
                        sortingId: value.sortId,
                    },
                }),
            );

            if (value.printReceiptReport) {
                promises.push(
                    generateReport.mutateAsync({
                        reportId: 33,
                        parameters: {
                            invoiceId: invoiceId,
                            sortingId: value.sortId,
                        },
                    }),
                );
            }

            await Promise.all(promises);

            onClose();
        },
    });

    return (
        <Dialog open={open} onClose={onClose} maxWidth={"sm"} fullWidth>
            <DialogTitle>Invoice Report</DialogTitle>
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    void form.handleSubmit();
                }}
            >
                <DialogContent>
                    <Stack spacing={2}>
                        <form.Field name={"reportId"}>
                            {(field) => (
                                <FormControl>
                                    <FormLabel>Report</FormLabel>
                                    <RadioGroup
                                        value={field.state.value ?? ""}
                                        onChange={(e) => field.handleChange(+e.target.value)}
                                    >
                                        <FormControlLabel control={<Radio />} label={"Detailed Report"} value={31} />
                                        <FormControlLabel control={<Radio />} label={"One Page Report"} value={32} />
                                    </RadioGroup>
                                </FormControl>
                            )}
                        </form.Field>
                        <form.Subscribe selector={(state) => state.values.reportId}>
                            {(reportId) =>
                                reportId == 31 && (
                                    <form.Field name={"sortId"}>
                                        {(field) => (
                                            <FormControl>
                                                <FormLabel>Sort</FormLabel>
                                                <RadioGroup
                                                    value={field.state.value ?? ""}
                                                    onChange={(e) => field.handleChange(+e.target.value)}
                                                >
                                                    <FormControlLabel
                                                        control={<Radio />}
                                                        label={"By Staff"}
                                                        value={1}
                                                    />
                                                    <FormControlLabel
                                                        control={<Radio />}
                                                        label={"By Staff then by Work Package"}
                                                        value={2}
                                                    />
                                                    <FormControlLabel
                                                        control={<Radio />}
                                                        label={"By Work Package"}
                                                        value={3}
                                                    />
                                                    <FormControlLabel
                                                        control={<Radio />}
                                                        label={"By Work Package then by Staff"}
                                                        value={4}
                                                    />
                                                </RadioGroup>
                                            </FormControl>
                                        )}
                                    </form.Field>
                                )
                            }
                        </form.Subscribe>
                        <form.Field name={"printReceiptReport"}>
                            {(field) => (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={field.state.value ?? false}
                                            onChange={(e) => field.handleChange(e.target.checked)}
                                        />
                                    }
                                    label={"Print Receipts Report"}
                                />
                            )}
                        </form.Field>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button variant={"outlined"} onClick={onClose}>
                        Close
                    </Button>
                    <Button variant={"contained"} type={"submit"}>
                        Print
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
}
